import { z } from 'zod'
import {
  AppraisalType,
  LuxuryLevel,
  WiggotPropertyType,
  PreservationState,
  View
} from '../../enums/eschema.enum'

export const zCapitalGains = () =>
  z.object({
    periods: z.number(),
    annual: z.number().nullable(),
    timeSeries: z.array(
      z.object({
        year: z.number(),
        value: z.number()
      })
    )
  })

export const zCapitalGainsResponse = () =>
  z.object({
    success: z.boolean(),
    message: z.string(),
    data: zCapitalGains()
  })

export type CapitalGains = z.infer<ReturnType<typeof zCapitalGains>>
export type CapitalGainsResponse = z.infer<
  ReturnType<typeof zCapitalGainsResponse>
>

export const zProperty = () =>
  z.object({
    id: z.string().optional(),
    coverImage: z.string().optional(),
    propertyType: z.nativeEnum(WiggotPropertyType),
    latitude: z.number(),
    longitude: z.number(),
    state: z.string(),
    city: z.string(),
    neighborhood: z.string(),
    postalCode: z.string(),
    street: z.string(),
    externalNumber: z.string(),
    constructionSize: z.number(),
    landSize: z.number(),
    bedrooms: z.number(),
    bathrooms: z.number(),
    parkingSpaces: z.number()
  })

export const zPropertyDetails = () =>
  zProperty().extend({
    yearBuilt: z.number(),
    preservation: z.nativeEnum(PreservationState).optional(),
    halfBathrooms: z.number(),
    hasServiceRoom: z.boolean(),
    hasBalcony: z.boolean(),
    hasTerrace: z.boolean(),
    viewTypes: z.array(z.nativeEnum(View)).optional(),
    amenities: z.array(z.string()).optional(),
    hasBeachAccess: z.boolean(),
    luxuryLevel: z.nativeEnum(LuxuryLevel).optional(),
    builtSurface: z.number().optional()
  })

export const zPropertyDetailsNoId = () => zPropertyDetails().omit({ id: true })

const zComparableStats = () =>
  z.object({
    listingCount: z.number(),
    appraisalCount: z.number(),
    data: z.array(
      z.object({
        price: z.number(),
        pricePerSquareMeter: z.number(),
        builtYear: z.number(),
        area: z.number()
      })
    )
  })

export const zAppraisal = () =>
  z.object({
    id: z.number(),
    operationType: z.nativeEnum(AppraisalType),
    pdfId: z.string().optional(),
    pdfUrl: z.string().optional(),
    createdAt: z.string(),
    price: z.number(),
    minPrice: z.number(),
    maxPrice: z.number(),
    pricePerSquareMeter: z.number().optional(),
    rentalPrice: z.number().optional(),
    rentalPricePerSquareMeter: z.number().optional(),
    property: zPropertyDetailsNoId(),
    saleComparableStats: zComparableStats().nullable().optional()
  })

export type Appraisal = z.infer<ReturnType<typeof zAppraisal>>
